import {
  LabJobStatus,
  MeasureFormatter,
  MeasureIcon,
} from '@principle-theorem/principle-core/interfaces';
import {
  ILabJobDimension,
  MeasureTransformMap,
} from '@principle-theorem/reporting/interfaces';
import { QueryOptionResolver } from '../../query-option-resolvers';
import { BaseDimensionMeasures } from '../base-measures';
import { MeasurePath } from '../data-accessor-factory';
import {
  CanBeChartedProperty,
  CanDoAllProperty,
  CanQueryByTimestampProperty,
} from '../measure-properties';
import { MeasurePropertyFactory } from '../measure-property-factory';

export class LabJobDimensionMeasures
  extends BaseDimensionMeasures
  implements
    MeasureTransformMap<
      Pick<
        ILabJobDimension,
        | 'status'
        | 'cost'
        | 'createdAt'
        | 'dueDate'
        | 'sentAt'
        | 'receivedAt'
        | 'issuedAt'
        | 'lab'
        | 'patient'
        | 'practitioner'
      >
    >
{
  get ref(): CanBeChartedProperty {
    const docRef = this.measureRef(MeasurePath.docRef('ref'));
    return MeasurePropertyFactory.docRef(
      {
        id: this._pathWithPrefix('ref'),
        label: 'Lab Job Ref',
        isFilterable: false,
        availableAsColumn: false,
        availableAsChart: false,
      },
      docRef,
      this.buildQuery().attributes([docRef.attributePath]).get()
    );
  }

  get status(): CanDoAllProperty {
    const measure = this.measureRef('status');
    return MeasurePropertyFactory.enum<LabJobStatus>(
      {
        id: this._pathWithPrefix('status'),
        label: 'Lab Job Status',
        summary: '',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      LabJobStatus.Sending
    );
  }

  get cost(): CanBeChartedProperty {
    const propertyName = 'cost';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.number(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Lab Job Cost',
        summary: '',
        formatter: MeasureFormatter.Currency,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get createdAt(): CanQueryByTimestampProperty {
    const propertyName = 'createdAt';
    const measure = this.measureRef(MeasurePath.timestamp(propertyName));
    return MeasurePropertyFactory.timestamp(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Lab Job Created Date',
        summary: 'Date that the lab job was created',
        formatter: MeasureFormatter.Day,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get dueDate(): CanQueryByTimestampProperty {
    const propertyName = 'dueDate';
    const measure = this.measureRef(MeasurePath.timestamp(propertyName));
    return MeasurePropertyFactory.timestamp(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Lab Job Due Date',
        summary: 'Date that the lab job is due',
        formatter: MeasureFormatter.Day,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get sentAt(): CanQueryByTimestampProperty {
    const propertyName = 'sentAt';
    const measure = this.measureRef(MeasurePath.timestamp(propertyName));
    return MeasurePropertyFactory.timestamp(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Lab Job Sent Date',
        summary: 'Date that the lab job was sent to the lab',
        formatter: MeasureFormatter.Day,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get receivedAt(): CanQueryByTimestampProperty {
    const propertyName = 'receivedAt';
    const measure = this.measureRef(MeasurePath.timestamp(propertyName));
    return MeasurePropertyFactory.timestamp(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Lab Job Received Date',
        summary: 'Date that the lab job was received from the lab',
        formatter: MeasureFormatter.Day,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get issuedAt(): CanQueryByTimestampProperty {
    const propertyName = 'issuedAt';
    const measure = this.measureRef(MeasurePath.timestamp(propertyName));
    return MeasurePropertyFactory.timestamp(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Lab Job Issued Date',
        summary: 'Date that the lab job was issued to the patient',
        formatter: MeasureFormatter.Day,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get lab(): CanDoAllProperty {
    const name = this.measureRef('lab.name');
    const docRef = this.measureRef(MeasurePath.docRef('lab.ref'));
    return MeasurePropertyFactory.docRef(
      {
        id: this._pathWithPrefix('lab'),
        label: 'Lab Name',
        summary: 'The name of the lab associated with the lab job',
        optionResolver: QueryOptionResolver.labs(),
      },
      docRef,
      this.buildQuery()
        .attributes([name.attributePath, docRef.attributePath])
        .get(),
      name,
      'No Lab'
    );
  }

  get patient(): CanDoAllProperty {
    const name = this.measureRef('patient.name');
    const docRef = this.measureRef(MeasurePath.docRef('patient.ref'));
    return MeasurePropertyFactory.docRef(
      {
        id: this._pathWithPrefix('patient'),
        label: 'Patient Name',
        summary: 'The name of the patient associated with the lab job',
        icon: MeasureIcon.Person,
        optionResolver: QueryOptionResolver.patients(),
      },
      docRef,
      this.buildQuery()
        .attributes([name.attributePath, docRef.attributePath])
        .get(),
      name,
      'No Patient'
    );
  }

  get hasPractitioner(): CanDoAllProperty {
    const propertyName = 'hasPractitioner';
    const measure = this.measureRef(MeasurePath.docRef('practitioner.ref'));
    return MeasurePropertyFactory.propertyExists(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Has Practitioner',
        summary: 'Whether the lab job has a practitioner assigned',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get practitioner(): CanDoAllProperty {
    const name = this.measureRef('practitioner.name');
    const docRef = this.measureRef(MeasurePath.docRef('practitioner.ref'));
    return MeasurePropertyFactory.docRef(
      {
        id: this._pathWithPrefix('practitioner'),
        label: 'Practitioner Name',
        summary: 'The name of the practitioner associated with the lab job',
        optionResolver: QueryOptionResolver.staff(),
        icon: MeasureIcon.Person,
      },
      docRef,
      this.buildQuery()
        .attributes([name.attributePath, docRef.attributePath])
        .get(),
      name,
      'No Practitioner'
    );
  }

  get type(): CanDoAllProperty {
    const propertyName = 'type.name';
    const measure = this.measureRef(propertyName);
    return MeasurePropertyFactory.string(
      {
        id: this._pathWithPrefix('type'),
        label: 'Lab Job Type',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }
}
